import { GovIcon } from '@gov-design-system-ce/react';
import {
	useConfiguration,
	useTranslationWithNamespace,
} from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useRegistrRidicuContext } from '@gov-nx/module/page';
import {
	Button,
	CommaSpace,
	DataTable,
	InfoBanner,
	Space,
	Tile,
	Tiles,
} from '@gov-nx/ui/web';
import { isInPastOrNearFuture, toDateReadable } from '@gov-nx/utils/common';
import { pageRouteResolver } from '@gov-nx/web';

export interface DriversRegistryCompetencyViewProps {
	code: PageCode;
}

export const DriversRegistryCompetencyView = ({
	code,
}: DriversRegistryCompetencyViewProps) => {
	const { zadostNovyRidicskyPrukazUrl } = useConfiguration();
	const { data } = useRegistrRidicuContext();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const ts = getLocalizeCurried(code);

	const showWarning = isInPastOrNearFuture(data?.license?.validTo);

	return (
		<section className="flex flex-col gap-6">
			<Tiles>
				<Tile
					name={ts('ridicsky-prukaz')}
					icon={{ name: 'chevron-right', type: 'components' }}
					to={pageRouteResolver('profil.doklady.ridicsky-prukaz')}
					headlineLink={true}
					customClasses="[ last-of-type:border-b ]">
					<p className="text-(--text-secondary) mb-2">
						{ts('cislo-prukazu')}: <strong>{data?.license?.id}</strong>
						{data?.license?.validFrom && (
							<>
								<CommaSpace />
								{ts('platnost-od')}
								<Space />
								<strong>{toDateReadable(data.license.validFrom)}</strong>
							</>
						)}
						{data?.license?.validTo && (
							<>
								<CommaSpace />
								{ts('platnost-do')}
								<Space />
								<strong>{toDateReadable(data.license.validTo)}</strong>
								<Space />
								{showWarning && (
									<GovIcon
										type="components"
										name="exclamation-triangle-fill"
										slot="icon"
										color="warning"
									/>
								)}
							</>
						)}
					</p>
					{data?.license?.canChange && !!zadostNovyRidicskyPrukazUrl && (
						<Button
							asChild
							type="outlined"
							color="primary"
							iconEnd={
								<GovIcon
									name="box-arrow-up-right"
									type="components"
								/>
							}
							aria-label={ts('akce.zazadat-o-novy')}
							className="mt-(--templates-margin-s)!">
							<a
								href={zadostNovyRidicskyPrukazUrl}
								target="_blank"
								rel="noopener noreferrer">
								{ts('akce.zazadat-o-novy')}
							</a>
						</Button>
					)}
				</Tile>
			</Tiles>

			<section className="py-2">
				<h2 className="text-headline-s!">{ts('ridicska-opravneni')}</h2>
				{data?.hasCompetencies ? (
					<DataTable responsive>
						<tbody>
							{data?.competencies?.map((competency) => (
								<tr key={competency.id}>
									<td className="sm:w-1/2">
										{ts('data.skupina')} {competency.group}
									</td>
									<td className="sm:w-1/2">
										{competency.validFrom && (
											<strong>
												{ts('data.od')} {toDateReadable(competency.validFrom)}
											</strong>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</DataTable>
				) : (
					<InfoBanner
						headline={ts('stav.chyba.zadne-ridicske-opravneni')}
						text={ts('stav.chyba.zadne-ridicske-opravneni-popis')}
					/>
				)}
			</section>

			<section className="py-2">
				<h2 className="text-headline-s!">{ts('profesni-zpusobilost')}</h2>
				{data?.hasQualification ? (
					<DataTable responsive>
						<tbody>
							{data?.qualification?.groups.map((group) => (
								<tr key={data.qualification?.id}>
									<td className="sm:w-1/2">
										{ts('data.skupina')} {group}
									</td>
									<td className="sm:w-1/2">
										{data?.qualification?.validTo && (
											<strong>
												{ts('data.do')}
												<Space />
												{toDateReadable(data.qualification.validTo)}
											</strong>
										)}
									</td>
								</tr>
							))}
						</tbody>
					</DataTable>
				) : (
					<InfoBanner
						headline={ts('stav.chyba.zadna-profesni-zpusobilost')}
						text={ts('stav.chyba.zadna-profesni-zpusobilost-popis')}
					/>
				)}
			</section>
		</section>
	);
};
